import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { trackCheckoutStart } from '@/lib/gtag';

export type SubscriptionTier = 'free' | 'prime' | 'pro';
export type Currency = 'brl' | 'usd';

interface SubscriptionLimits {
  max_projects: number;
  max_members: number;
  max_tabs: number;
  max_topics_per_tab: number;
  features: string[];
}

interface SubscriptionData {
  tier: SubscriptionTier;
  subscribed: boolean;
  subscription_end: string | null;
  limits: SubscriptionLimits;
}

const TIER_LIMITS: Record<SubscriptionTier, SubscriptionLimits> = {
  free: { max_projects: 1, max_members: 2, max_tabs: 3, max_topics_per_tab: 5, features: [] },
  prime: { max_projects: 10, max_members: 3, max_tabs: 20, max_topics_per_tab: 30, features: ['basic_export'] },
  pro: { max_projects: 80, max_members: 10, max_tabs: 20, max_topics_per_tab: 30, features: ['customization', 'analytics', 'priority_support', 'advanced_export', 'clients_portfolio'] }
};

// Prices by currency - Only BRL for Brazil, USD for everyone else
export const STRIPE_PRICES = {
  brl: {
    prime: 'price_1SdKmgAyEdsdIgG2pKmP1G0p',
    pro: 'price_1SdKocAyEdsdIgG2M3bZB2rd'
  },
  usd: {
    prime: 'price_1Sdlc7AyEdsdIgG2Nq2U0bXU',
    pro: 'price_1SdlcHAyEdsdIgG2kiRQlEUg'
  }
};

// Price display values by currency
export const PRICE_DISPLAY = {
  brl: {
    prime: { value: 29.99, formatted: 'R$ 29,99' },
    pro: { value: 67.90, formatted: 'R$ 67,90' }
  },
  usd: {
    prime: { value: 19.99, formatted: '$ 19.99' },
    pro: { value: 37.99, formatted: '$ 37.99' }
  }
};

// Get currency from country code - Only Brazil uses BRL
export const getCurrencyFromCountry = (countryCode: string | null | undefined): Currency => {
  return countryCode === 'BR' ? 'brl' : 'usd';
};

const CACHE_KEY = 'taskeer_subscription_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

interface CachedSubscription extends SubscriptionData {
  cachedAt: number;
  userId: string;
}

const getCachedSubscription = (userId: string): SubscriptionData | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const data: CachedSubscription = JSON.parse(cached);
    
    // Verificar se o cache é do mesmo usuário e ainda é válido
    if (data.userId !== userId) return null;
    if (Date.now() - data.cachedAt > CACHE_DURATION) return null;
    
    return {
      tier: data.tier,
      subscribed: data.subscribed,
      subscription_end: data.subscription_end,
      limits: TIER_LIMITS[data.tier]
    };
  } catch {
    return null;
  }
};

const setCachedSubscription = (userId: string, data: SubscriptionData) => {
  try {
    const cacheData: CachedSubscription = {
      ...data,
      cachedAt: Date.now(),
      userId
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch {
    // Ignorar erros de localStorage
  }
};

const clearSubscriptionCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // Ignorar erros
  }
};

export const useSubscription = () => {
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [userCountry, setUserCountry] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Currency based on user's saved country (BR = BRL, else USD)
  const currency = getCurrencyFromCountry(userCountry);

  // Fetch user's country from profile
  useEffect(() => {
    const fetchUserCountry = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('country')
            .eq('id', session.user.id)
            .single();
          
          if (profile?.country) {
            console.log('[useSubscription] User country from profile:', profile.country);
            setUserCountry(profile.country);
          }
        }
      } catch (err) {
        console.error('[useSubscription] Failed to fetch user country:', err);
      }
    };
    fetchUserCountry();
  }, []);

  const checkSubscription = async (forceRefresh = false) => {
    try {
      // Verificar se há uma sessão válida primeiro
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        clearSubscriptionCache();
        setSubscription({
          tier: 'free',
          subscribed: false,
          subscription_end: null,
          limits: TIER_LIMITS.free
        });
        setLoading(false);
        return;
      }

      const userId = session.user.id;

      // Verificar cache primeiro (se não for forceRefresh)
      if (!forceRefresh) {
        const cached = getCachedSubscription(userId);
        if (cached) {
          setSubscription(cached);
          setLoading(false);
          // Atualizar em background após retornar cache
          refreshSubscriptionInBackground(userId);
          return;
        }
      }

      setLoading(true);
      await fetchAndUpdateSubscription(userId);
    } catch (error) {
      console.error('Error checking subscription:', error);
      if (!subscription) {
        setSubscription({
          tier: 'free',
          subscribed: false,
          subscription_end: null,
          limits: TIER_LIMITS.free
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAndUpdateSubscription = async (userId: string) => {
    const { data, error } = await supabase.functions.invoke('check-subscription');
    
    if (error) {
      if (error.message?.includes('Authentication') || error.message?.includes('session')) {
        clearSubscriptionCache();
        setSubscription({
          tier: 'free',
          subscribed: false,
          subscription_end: null,
          limits: TIER_LIMITS.free
        });
        return;
      }
      throw error;
    }

    const tier = data.tier || 'free';
    const newSubscription: SubscriptionData = {
      tier,
      subscribed: data.subscribed || false,
      subscription_end: data.subscription_end,
      limits: TIER_LIMITS[tier as SubscriptionTier]
    };
    
    setSubscription(newSubscription);
    setCachedSubscription(userId, newSubscription);
  };

  const refreshSubscriptionInBackground = async (userId: string) => {
    try {
      await fetchAndUpdateSubscription(userId);
    } catch (error) {
      console.error('Background subscription refresh failed:', error);
    }
  };

  useEffect(() => {
    checkSubscription();

    // Check every 60 seconds
    const interval = setInterval(() => checkSubscription(true), 60000);

    // Limpar cache no logout
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        clearSubscriptionCache();
        setSubscription({
          tier: 'free',
          subscribed: false,
          subscription_end: null,
          limits: TIER_LIMITS.free
        });
      }
    });

    return () => {
      clearInterval(interval);
      authSubscription.unsubscribe();
    };
  }, []);

  const createCheckout = async (tier: 'prime' | 'pro'): Promise<boolean> => {
    try {
      // Check if user is logged in first
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: 'Login necessário',
          description: 'Você precisa estar logado para assinar um plano.',
          variant: 'destructive'
        });
        return false;
      }

      setCheckoutLoading(true);

      const value = PRICE_DISPLAY[currency][tier].value;

      // Track checkout initiation for Google Ads
      trackCheckoutStart(tier, value);

      // Backend determines the correct price based on user's saved country
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { tier }
      });

      if (error) throw error;
      
      if (data.url) {
        window.open(data.url, '_blank');
      }
      return true;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: 'Erro ao criar checkout',
        description: 'Não foi possível iniciar o processo de pagamento.',
        variant: 'destructive'
      });
      return false;
    } finally {
      setCheckoutLoading(false);
    }
  };

  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');

      if (error) throw error;
      
      if (data.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({
        title: 'Erro ao abrir portal',
        description: 'Não foi possível abrir o portal de gerenciamento.',
        variant: 'destructive'
      });
    }
  };

  // Retorna um valor padrão enquanto carrega do cache
  const currentSubscription = subscription || {
    tier: 'free' as SubscriptionTier,
    subscribed: false,
    subscription_end: null,
    limits: TIER_LIMITS.free
  };

  return {
    subscription: currentSubscription,
    loading: loading && !subscription, // Só mostra loading se não tiver cache
    checkoutLoading,
    checkSubscription,
    createCheckout,
    openCustomerPortal,
    currency,
    prices: PRICE_DISPLAY[currency]
  };
};
